import { InfoIcon, Minus, Pencil, Plus } from "lucide-react"
import { useTranslations } from "next-intl"
import React, { memo, useEffect, useRef } from "react"

import { requestDineInItemAction } from "@/app/[locale]/customer/actions"
import { CustomerRequestSession } from "@/components/customer/CustomerRequestSession"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { CONST } from "@/constants"
import type { CustomerOngoingOrderByStatusLoader } from "@/data/customerDataLoader"
import { usePendingOrder } from "@/hooks/customer/usePendingOrder"
import { useToast } from "@/hooks/use-toast"
import { useCart } from "@/hooks/useCart"
import { useServerAction } from "@/hooks/useServerAction"
import { Link, useRouter } from "@/i18n/routing"
import { ROUTES } from "@/routes"
import { ClientUtils } from "@/utils/client/ClientUtils"
import { PriceSharedUtils } from "@/utils/shared/PriceSharedUtils"
import { useCustomerProfile } from "@/hooks/useGlobalStore"
import { Tip } from "../Tip"
import { useQueryState } from "nuqs"
import { useLocale } from "@/hooks/useLocale"
import { Select, SelectItem, SelectContent, SelectTrigger, SelectValue } from "../ui/select"

export const CustomerRequestDialogQuery = "customer_request_dialog"
export const CustomerCart = memo(function CustomerCart({
  ongoingOrder,
  closeCart,
}: {
  ongoingOrder: CustomerOngoingOrderByStatusLoader | null
  closeCart: () => void
}) {
  const t = useTranslations("customer")
  const { toast } = useToast()
  const tCommon = useTranslations("common")
  const contextCustomerProfile = useCustomerProfile({ required: false })
  const ongoingOrderRef = useRef<CustomerOngoingOrderByStatusLoader | null>(null)
  const pendingOrderRef = useRef<CustomerOngoingOrderByStatusLoader | null>(null)
  const { pendingOrder } = usePendingOrder()
  const locale = useLocale()

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useQueryState(CustomerRequestDialogQuery)
  const [isOrderConfirmDialogOpen, setIsOrderConfirmDialogOpen] = React.useState(false)
  const { isPending: isRequestPending, serverAction: requestDineInItems } = useServerAction(requestDineInItemAction)

  const {
    deleteFromCart,
    clearCart,
    calculateTotalEffectivePriceDollars,
    calculateTotalPriceDollars,
    incrementInCart,
    decrementInCart,
    getCartMerchantName,
    getCartItems,
    getCartMerchantId,
    navigateToEditDish,
    getCartMerchantDefaultDiscount,
    getCartType,
    setCartType,
  } = useCart()
  const cartItems = getCartItems()

  const cartMerchantName = getCartMerchantName()
  const cartMerchantId = getCartMerchantId()
  const cartMerchantDefaultDiscount = getCartMerchantDefaultDiscount()
  const doesCartContainDineInItems = cartItems.some((item) => item.dish.category.categoryType === "DINE_IN")
  const cartType = getCartType()
  const router = useRouter()

  // Clear cart when an accepted dine in order is created
  useEffect(() => {
    if (pendingOrder && pendingOrder.id !== pendingOrderRef.current?.id) {
      pendingOrderRef.current = pendingOrder
    }

    if (ongoingOrder && pendingOrderRef.current && !ongoingOrderRef.current) {
      ongoingOrderRef.current = ongoingOrder
      setIsOrderConfirmDialogOpen(false)
      closeCart()
      clearCart()
      router.push(ROUTES.customerOngoingOrder)
    }
  }, [ongoingOrder, pendingOrder, pendingOrderRef, ongoingOrderRef, clearCart, router])

  const handleIncrement = (id: string) => {
    incrementInCart({ id, quantity: 1 })
  }

  const handleDecrement = (id: string) => {
    decrementInCart({ id, quantity: 1 })
  }

  const handleClearCart = () => {
    clearCart()
  }

  const handleViewPendingOrder = () => {
    setIsConfirmDialogOpen("true")
  }

  const handleRequestDineIn = () => {
    setIsConfirmDialogOpen("true")
  }

  const handleOrderItems = () => {
    if (ongoingOrder?.initiateCheckout) {
      toast({
        title: t("orders.cart.checkout_in_progress.title"),
        description: t("orders.cart.checkout_in_progress.description"),
        variant: "destructive",
      })
      return
    }
    setIsOrderConfirmDialogOpen(true)
  }
  const handleConfirmOrder = () => {
    requestDineInItems({ cartItems })
    setIsOrderConfirmDialogOpen(false)
    clearCart()
    closeCart()
  }

  if (cartItems.length === 0 && !ongoingOrder && !pendingOrder) {
    return <div className="p-4 text-center text-muted-foreground">{t("orders.cart.empty")}</div>
  }

  return (
    <>
      <div className="flex flex-col gap-4 p-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium">{t("orders.cart.title")}</h2>
          </div>
          <Button variant="destructive" size="sm" onClick={handleClearCart} disabled={!!pendingOrder}>
            {t("orders.cart.clear")}
          </Button>
        </div>

        {cartMerchantName && cartMerchantId && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground">{t("orders.cart.from")}</div>
              <Link href={ROUTES.merchantPublic(cartMerchantId)} className="underline">
                {cartMerchantName}
              </Link>
            </div>
            {!ongoingOrder && (
              <div className="flex items-center gap-2">
                <Select value={cartType} onValueChange={setCartType}>
                  <SelectTrigger className="">
                    <SelectValue placeholder={t("orders.cart.select_cart_type")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DINE_IN">{t("orders.cart.cart_type.DINE_IN")}</SelectItem>
                    <SelectItem value="TAKE_OUT">{t("orders.cart.cart_type.TAKE_OUT")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <div className="max-h-[200px] overflow-y-auto overflow-x-hidden">
          {cartItems.length === 0 && (
            <div className="flex items-center justify-center py-2">
              <p className="text-sm text-muted-foreground">{t("orders.cart.empty")}</p>
            </div>
          )}
          {cartItems.map((item, idx) => (
            <div key={item.id}>
              <div className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <span
                    className="inline-flex gap-1 hover:cursor-pointer hover:underline"
                    onClick={() => navigateToEditDish(item)}
                  >
                    <h3 className="text-sm font-medium">
                      {ClientUtils.getLocalizedValue(locale, {
                        en: item.dish.nameEN,
                        fr: item.dish.nameFR,
                      })}
                    </h3>
                    <Pencil className="size-4 text-muted-foreground" />
                  </span>
                  <p className="text-sm text-muted-foreground">
                    $
                    {ClientUtils.formatPrice(
                      PriceSharedUtils.getDollarsPrice(
                        PriceSharedUtils.computeEffectivePriceCents(item.dish.priceCents, item.dish.discount)
                      ).toString()
                    )}
                    x {item.quantity}
                  </p>
                  <div className="flex flex-col gap-2">
                    {item.addOns.map((addOn) => (
                      <div key={addOn.id} className="flex items-center gap-2">
                        <p className="text-sm">
                          {ClientUtils.getLocalizedValue(locale, {
                            en: addOn.nameEN,
                            fr: addOn.nameFR,
                          })}
                        </p>
                        {/* <p className="text-sm line-through">
                        {ClientUtils.formatCurrency(
                          PriceSharedUtils.getDollarsPrice(addOn.priceCents).toString()
                        )}
                      </p> */}
                        <p className="text-sm text-muted-foreground">
                          $
                          {ClientUtils.formatPrice(
                            PriceSharedUtils.getDollarsPrice(
                              PriceSharedUtils.computeEffectivePriceCents(
                                addOn.priceCents,
                                cartMerchantDefaultDiscount ?? 0
                              )
                            ).toString()
                          )}{" "}
                          x {addOn.quantity}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="relative flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 rounded-md border p-1">
                    <Button variant="ghost" size="lg" onClick={() => handleDecrement(item.id)} className="size-3 p-3">
                      <Minus />
                    </Button>
                    <span className="w-6 text-center text-sm">{item.quantity}</span>
                    <Button variant="ghost" size="lg" onClick={() => handleIncrement(item.id)} className="size-3 p-3">
                      <Plus />
                    </Button>
                  </div>
                </div>
              </div>
              {idx !== cartItems.length - 1 && (
                <div className="mx-4 my-1 border-t border-dashed border-muted-foreground" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-1 flex flex-col gap-2 border-t pt-4">
          <div>
            <div className="flex items-center justify-between">
              <span className="font-medium">{t("orders.cart.total")}</span>
              <div className="flex items-center gap-2">
                {doesCartContainDineInItems && (
                  <span className="text-sm text-muted-foreground line-through">
                    ${ClientUtils.formatPrice(calculateTotalPriceDollars().toString())}
                  </span>
                )}
                <span className="text-lg font-medium">
                  ${ClientUtils.formatPrice(calculateTotalEffectivePriceDollars().toString())}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t("orders.cart.processing_fees")}:</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  $
                  {ClientUtils.formatPrice(
                    PriceSharedUtils.calculatePaymentAmounts({
                      totalEffectiveAmountCents: PriceSharedUtils.getCentsPrice(calculateTotalEffectivePriceDollars()),
                      tipRate: 0,
                    }).dollars.stripeFeeAmount.customer
                  )}
                </span>
              </div>
            </div>
          </div>
          {doesCartContainDineInItems && (
            <span className="font-bold">
              {t("orders.request_session.estimated_saving")}{" "}
              <span className="text-green-600">
                $
                {ClientUtils.formatPrice(
                  (calculateTotalPriceDollars() - calculateTotalEffectivePriceDollars()) *
                    CONST.ESTIMATED_SAVING_MULTIPLIER
                )}
              </span>
              <Tip content={t("orders.request_session.estimated_saving_tooltip")} className="ml-1 translate-y-0.5">
                <InfoIcon className="size-4 text-muted-foreground" />
              </Tip>
            </span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {pendingOrder && <Button onClick={handleViewPendingOrder}>{t("orders.cart.view_pending_order")}</Button>}
          {ongoingOrder ? (
            <>
              <Button onClick={handleOrderItems} disabled={cartItems.length === 0 || ongoingOrder?.initiateCheckout}>
                {t("orders.cart.actions.order")}
              </Button>
              <Link href={ROUTES.customerOngoingOrder}>
                <Button className="w-full animate-pulse" onClick={closeCart}>
                  {t("orders.ongoing.view")}
                </Button>
              </Link>
            </>
          ) : (
            <>
              {!pendingOrder && contextCustomerProfile?.customerProfile?.id && (
                <Button onClick={handleRequestDineIn}>{t("orders.cart.actions.confirm")}</Button>
              )}
              {!contextCustomerProfile?.customerProfile?.id && (
                <Link href={ROUTES.loginCustomer}>
                  <Button className="w-full">{tCommon("auth.login.title")}</Button>
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      {isConfirmDialogOpen && (
        <CustomerRequestSession
          couponMerchant={!doesCartContainDineInItems && !pendingOrder}
          isOpen={!!isConfirmDialogOpen}
          pendingOrder={pendingOrder as CustomerOngoingOrderByStatusLoader}
          onClose={() => setIsConfirmDialogOpen(null)}
          cartItems={cartItems}
          cartMerchantId={cartMerchantId || ""}
          cartType={cartType}
          cartMerchantName={cartMerchantName || ""}
          calculateTotalPriceDollars={calculateTotalPriceDollars}
          calculateTotalEffectivePriceDollars={calculateTotalEffectivePriceDollars}
        />
      )}

      <Dialog open={isOrderConfirmDialogOpen} onOpenChange={setIsOrderConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("orders.cart.actions.confirm")}</DialogTitle>
            <DialogDescription>{t("orders.cart.actions.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOrderConfirmDialogOpen(false)}>
              {t("orders.cart.actions.cancel")}
            </Button>
            <Button onClick={handleConfirmOrder} disabled={isRequestPending}>
              {isRequestPending ? t("orders.cart.actions.ordering") : t("orders.cart.actions.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})
